/* Per-dataset movement, and the honest reason it is not free.
 *
 * **There is no batch delta endpoint.** `EstateSummary` carries a `delta` for the *estate*;
 * `/scores/latest` carries a value and a coverage per dataset and no previous value; the only
 * per-dataset movement in `scoring.yaml` is `GET /datasets/{id}/trend`, which is one request per
 * dataset. That is why the ledger shipped without a change column and said so.
 *
 * The answer here is not to fabricate one and not to give up on it, but to make the cost explicit
 * and bounded:
 *
 * * **Automatic while it is cheap.** Up to `AUTO_MOVEMENT_LIMIT` visible rows, the reads run on
 *   mount and every row has a real delta.
 * * **Asked for beyond that.** Past the limit nothing is requested until a reader presses the
 *   control, and the control says how many requests it will make. A screen that quietly issued 500
 *   requests on mount is the N+1 that "looks fine on a demo tenant and falls over on a real one".
 * * **Never partially claimed.** A row whose movement has not been read renders "not read" rather
 *   than an em dash, because a blank cell beside populated ones reads as *this one did not move*,
 *   which is a claim about the data made out of a limit of the request.
 *
 * The shape is deliberately what a future `POST /v1/scoring/movements` would fill: ids in, a map of
 * movements out. Replacing this file's internals with one request changes no caller.
 */

import { useQueries } from '@tanstack/react-query';

import { isTrend } from '@/shared/api/generated/scoring';
import { getOne } from '@/shared/api/http';

import { movementOf } from '../model/movement';
import type { Movement } from '../model/types';

/** How many datasets are read without being asked. Chosen as the number a reader can see and act on
 * at once rather than as a network budget: past this the page is a listing to page through, and a
 * delta on row 400 is not being read by anybody. */
export const AUTO_MOVEMENT_LIMIT = 40;

export interface MovementsResult {
  readonly byDataset: ReadonlyMap<string, Movement>;
  /** True while any of the reads is still in flight. */
  readonly loading: boolean;
  /** How many of the requested datasets came back. Reported so a partial answer is visible. */
  readonly resolved: number;
  readonly requested: number;
  /** True when at least one read failed. The rows that did resolve are still shown. */
  readonly failed: boolean;
}

/**
 * Movement for each id, as one map.
 *
 * `enabled` is the caller's decision, not this hook's: the toolbar owns whether the estate is small
 * enough to read automatically, and passing that in keeps the policy in one place instead of being
 * half here and half there.
 */
export function useMovements(
  datasetIds: readonly string[],
  days: number,
  enabled: boolean,
): MovementsResult {
  /* **An empty list when the reads are off, rather than `enabled: false` on every entry.** A
     disabled query still builds an observer, so a five-hundred-dataset estate would construct five
     hundred of them on every render to fetch nothing. The opt-in is only genuinely free if turning
     it off costs nothing. */
  const wanted = enabled ? datasetIds : [];

  const results = useQueries({
    queries: wanted.map((id) => ({
      /* The same key shape `useDatasetTrend` uses, so the drawer's own trend read for the selected
         row is a cache hit rather than a second request for bytes already in memory. */
      queryKey: ['scoring', 'score', id, 'trend', days],
      queryFn: () =>
        getOne(
          `/v1/scoring/datasets/${encodeURIComponent(id)}/trend?days=${String(days)}`,
          isTrend,
          'trend',
        ),
      /* A trend moves only when a scoring run completes, which is a scheduled sweep rather than a
         user action. Re-reading N of them on every remount of this screen buys nothing. */
      staleTime: 60_000,
      retry: false,
    })),
  });

  const byDataset = new Map<string, Movement>();
  let loading = false;
  let failed = false;

  results.forEach((result, index) => {
    const id = wanted[index];
    if (id === undefined) return;
    if (result.isLoading) loading = true;
    if (result.isError) failed = true;
    if (result.data !== undefined) byDataset.set(id, movementOf(result.data));
  });

  return {
    byDataset,
    loading,
    resolved: byDataset.size,
    requested: enabled ? datasetIds.length : 0,
    failed,
  };
}
